import clsx from "clsx";
import { useId, type ReactNode } from "react";

export interface RadioOption<T extends string = string> {
  value: T;
  label: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  /**
   * Accessible name for an option whose `label` has no nameable text — e.g.
   * math only, where KaTeX marks the visible glyphs `aria-hidden`. Leave unset
   * when the label has prose; it names the input itself.
   */
  ariaLabel?: string;
  /** `aria-keyshortcuts` for an option a key picks, e.g. "1" on a confidence rating. */
  keyShortcuts?: string;
}

export interface RadioGroupProps<T extends string = string> {
  name?: string;
  legend?: ReactNode;
  value: T | null;
  options: readonly RadioOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  /** Extra classes for each option's row (e.g. a phone-sized tap target). */
  optionClassName?: string;
  /** Extra classes for each option's `<label>`. */
  labelClassName?: string;
}

/** An accessible radio group — the MC answer control in practice and exams. */
export function RadioGroup<T extends string = string>({
  name,
  legend,
  value,
  options,
  onChange,
  className,
  optionClassName,
  labelClassName,
}: RadioGroupProps<T>) {
  const generated = useId();
  const groupName = name ?? generated;
  return (
    <fieldset className={clsx("flex flex-col gap-2", className)}>
      {legend && (
        <legend className="mb-1 text-sm font-medium text-ink-700 dark:text-ink-200">
          {legend}
        </legend>
      )}
      {options.map((option) => {
        const id = `${groupName}-${option.value}`;
        return (
          <div key={option.value} className={clsx("flex items-start gap-2", optionClassName)}>
            <input
              id={id}
              type="radio"
              name={groupName}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
              aria-label={option.ariaLabel}
              aria-keyshortcuts={option.keyShortcuts}
              className="mt-0.5 h-4 w-4 shrink-0 border-ink-400 text-accent-700 focus:ring-accent-500 dark:border-ink-600 dark:bg-ink-900"
            />
            <div>
              <label
                htmlFor={id}
                className={clsx("text-sm text-ink-800 dark:text-ink-100", labelClassName)}
              >
                {option.label}
              </label>
              {option.hint && (
                <p className="text-xs text-ink-500 dark:text-ink-400">{option.hint}</p>
              )}
            </div>
          </div>
        );
      })}
    </fieldset>
  );
}
